import React, { useCallback, useEffect, useRef, useState } from "react";

import {
  listRentalContracts,
  listKeyReceipts,
  fetchInspections,
  fetchAdminTenancyDocuments,
  getRentalContractPdfUrl,
  getKeyReceiptPdfUrl,
  getKeyReceiptSignedPdfUrl,
  fetchInspectionProtocolPdfUrl,
  fetchInspectionSignedPdfUrl,
  fetchAdminTenancyDocumentDownloadUrl,
  fetchAdminTerminationConfirmations,
  fetchAdminTerminationConfirmationPdfUrl,
} from "../../../api/adminData";
import { formatDateOnly } from "../../../utils/datetime";
import { buildTenancyDocuments } from "./tenancyDocuments";
import { Card, EmptyState, SECONDARY_BUTTON } from "./workspaceUi";

/**
 * The tenancy's Dokumente tab.
 *
 * FIVE SOURCES, ONE LIST. Mietvertrag, Kündigungsbestätigung (KND-4b),
 * Schlüsselquittung, Übergabe-/Abnahmeprotokoll and hand-uploaded files each
 * live in their own table with their own PDF pipeline;
 * `buildTenancyDocuments` maps them into one row shape. The composition is
 * the frontend's, the truth stays each family's — every open below goes back
 * to that family's own endpoint. A confirmation is never projected into the
 * uploads and never copied: its versions come from the one confirmations GET,
 * their delivery state from the history in that same answer.
 *
 * READ-ONLY. Opening this tab lists what exists; it never generates a PDF.
 * A document without a file offers no open button rather than a button that
 * would trigger a write or 404. Creating, generating and signing stay where
 * they already are, in the per-family sections on the tenant detail page.
 *
 * ONLY WHILE VISIBLE. The fetch is gated on `active`, so a tenancy the user
 * only glanced at in the list costs no document requests at all — and picking
 * a row while this tab is closed costs none either. Five parallel requests are
 * acceptable for ONE selected tenancy; five per row — or one per document
 * version — would not be. A signed URL is fetched only on "Öffnen".
 *
 * PARTIAL FAILURE IS NOT TOTAL FAILURE. The five run under `allSettled`: one
 * family being unavailable hides that family and says so, and the other four
 * still render. Everything is reported inside this tab — the other six tabs
 * and the list keep working.
 */

/** Which client answers "give me the file" for each family, and how it
 *  answers: three return `{ url }`, the inspection helper returns the string
 *  itself. Normalising that here keeps the caller from guessing. */
const OPENERS = {
  rental_contract: (id) => getRentalContractPdfUrl(id).then((r) => r?.url || ""),
  key_receipt: (id) => getKeyReceiptPdfUrl(id).then((r) => r?.url || ""),
  // M1c-B: a signed receipt has a second, different file. Its own endpoint,
  // not a URL assembled here — the backend decides what a signed PDF is.
  key_receipt_signed: (id) =>
    getKeyReceiptSignedPdfUrl(id).then((r) => r?.url || ""),
  inspection: (id) => fetchInspectionProtocolPdfUrl(id).then((u) => u || ""),
  // M1c-C: a signed protocol has a second, different file. Its own endpoint,
  // not a URL assembled here — the backend decides what a signed PDF is.
  inspection_signed: (id) =>
    fetchInspectionSignedPdfUrl(id).then((u) => u || ""),
  upload: (id) =>
    fetchAdminTenancyDocumentDownloadUrl(id).then((r) => r?.url || ""),
  // KND-4b: the confirmation's own signed-URL endpoint, by confirmation id —
  // the same one the termination panel opens. No second route, no copy.
  termination_confirmation: (id) =>
    fetchAdminTerminationConfirmationPdfUrl(id).then((r) => r?.url || ""),
};

/**
 * Which opener a row uses. Key receipts and inspections each have two, and only
 * because those records keep their generated PDF alongside the signed one;
 * contracts and uploads have a single file and answer to their `source` alone.
 */
function openerKey(item) {
  if (item.pdfVariant === "signed") {
    if (item.source === "key_receipt") return "key_receipt_signed";
    if (item.source === "inspection") return "inspection_signed";
  }
  return item.source;
}

const SOURCE_LABEL = {
  rental_contract: "Mietverträge",
  termination_confirmation: "Kündigungsbestätigungen",
  key_receipt: "Schlüsselquittungen",
  inspection: "Protokolle",
  upload: "Hochgeladene Dateien",
};

/**
 * Open the tab SYNCHRONOUSLY, before awaiting the signed URL, so the browser
 * does not treat it as a programmatic popup and block it. No
 * "noopener,noreferrer": noopener can make `window.open` return null, which
 * would break the open-then-navigate flow. Same handling
 * `RentalContractSection` and the inspection hook already use.
 */
function openBlankTab() {
  const w = window.open("", "_blank");
  if (w) {
    try {
      w.opener = null;
    } catch (_) {
      /* cross-origin / read-only opener — ignore */
    }
  }
  return w;
}

export default function TenancyDocumentsTab({ tenancyId, active }) {
  const [state, setState] = useState({
    loading: false,
    error: null,
    items: [],
    failed: [],
  });
  const [openingKey, setOpeningKey] = useState(null);
  const [openError, setOpenError] = useState(null);
  // The tenancy whose documents are currently loaded or loading. Guards
  // against both a repeat fetch on every tab switch and a response for the
  // previously selected row landing after a newer one.
  const loadedFor = useRef(null);

  const load = useCallback(async (id) => {
    loadedFor.current = id;
    setState({ loading: true, error: null, items: [], failed: [] });
    const settled = await Promise.allSettled([
      listRentalContracts(id),
      listKeyReceipts(id),
      fetchInspections({ tenancy_id: id }),
      fetchAdminTenancyDocuments(id),
      fetchAdminTerminationConfirmations(id),
    ]);
    if (loadedFor.current !== id) return;

    const [contracts, keyReceipts, inspections, uploads, terminationConfirmations] =
      settled.map((r) => (r.status === "fulfilled" ? r.value : null));
    const failed = [
      "rental_contract",
      "key_receipt",
      "inspection",
      "upload",
      "termination_confirmation",
    ].filter((_, i) => settled[i].status === "rejected");

    if (failed.length === settled.length) {
      setState({
        loading: false,
        // Every source is down — that is a connection, not a document, and
        // the first rejection carries the useful sentence.
        error:
          settled[0].reason?.message ||
          "Dokumente konnten nicht geladen werden.",
        items: [],
        failed,
      });
      return;
    }
    setState({
      loading: false,
      error: null,
      items: buildTenancyDocuments({
        contracts,
        terminationConfirmations,
        keyReceipts,
        inspections,
        uploads,
      }),
      failed,
    });
  }, []);

  useEffect(() => {
    if (!active || !tenancyId) return;
    if (loadedFor.current === tenancyId) return;
    load(tenancyId);
  }, [active, tenancyId, load]);

  // A different tenancy while this tab is closed: forget the old result so it
  // is never shown under the new contract, and let the effect above reload it
  // when the tab is opened again.
  useEffect(() => {
    if (loadedFor.current && loadedFor.current !== tenancyId) {
      loadedFor.current = null;
      setState({ loading: false, error: null, items: [], failed: [] });
    }
  }, [tenancyId]);

  const handleOpen = useCallback(async (item) => {
    const opener = OPENERS[openerKey(item)];
    if (!opener) return;
    const tab = openBlankTab();
    setOpeningKey(item.key);
    setOpenError(null);
    try {
      const url = await opener(item.id);
      if (!url) {
        tab?.close();
        setOpenError("Für dieses Dokument ist keine Datei hinterlegt.");
        return;
      }
      if (tab) tab.location = url;
      else setOpenError("Popup wurde blockiert. Bitte Popups erlauben.");
    } catch (e) {
      tab?.close();
      setOpenError(e?.message || "Dokument konnte nicht geöffnet werden.");
    } finally {
      setOpeningKey(null);
    }
  }, []);

  if (state.loading) {
    return (
      <div
        className="flex flex-col gap-2"
        aria-busy="true"
        data-testid="tenancy-documents-loading"
      >
        <span className="sr-only">Dokumente werden geladen …</span>
        {[0, 1, 2].map((i) => (
          <div
            key={i}
            aria-hidden="true"
            className="h-[56px] animate-pulse rounded-[10px] border border-border bg-muted/40"
          />
        ))}
      </div>
    );
  }

  if (state.error) {
    return (
      <div
        role="alert"
        data-testid="tenancy-documents-error"
        className="rounded-[10px] border border-rose-500/30 bg-rose-500/10 px-4 py-3 text-[13px] text-rose-700 dark:text-rose-300"
      >
        <p className="m-0">
          <strong className="font-semibold">Fehler beim Laden:</strong>{" "}
          {state.error}
        </p>
        <button
          type="button"
          onClick={() => load(tenancyId)}
          className={`mt-2 ${SECONDARY_BUTTON}`}
        >
          Erneut versuchen
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3" data-testid="tenancy-documents">
      {/* A framed block, like Finanzen and Notizen. Unframed rows floating in
          the panel were the reason this tab read as an embedded foreign module
          rather than the fifth tab of one workspace. The rows themselves lose
          their own card shell for the same reason — a bordered row inside a
          bordered card inside a bordered panel is three frames for one list. */}
      <Card
        title="Dokumente"
        subtitle="Mietvertrag, Kündigungsbestätigung, Protokolle, Schlüsselquittung und hochgeladene Dateien."
      >
      {state.items.length === 0 ? (
        <EmptyState
          testId="tenancy-documents-empty"
          title="Für dieses Mietverhältnis sind noch keine Dokumente verfügbar."
        />
      ) : (
        <ul className="m-0 flex list-none flex-col p-0">
          {state.items.map((item) => (
            <li
              key={item.key}
              className="flex items-center gap-3 border-b border-border py-[10px] first:pt-0 last:border-b-0 last:pb-0"
            >
              {/* A type marker, not decoration — but the row never relies on
                  it alone: the title says what the document is. */}
              <span
                aria-hidden="true"
                className="shrink-0 rounded-[5px] border border-border bg-muted px-[6px] py-[2px] text-[10px] font-semibold tracking-[0.4px] text-muted-foreground"
              >
                PDF
              </span>
              <div className="min-w-0 flex-1">
                <p className="m-0 truncate text-[13px] font-medium text-foreground">
                  {item.title}
                </p>
                <p className="m-0 truncate text-[12px] text-muted-foreground">
                  {[
                    item.subtitle,
                    item.statusLabel,
                    item.dateIso ? formatDateOnly(item.dateIso) : null,
                  ]
                    .filter(Boolean)
                    .join(" · ")}
                </p>
                {/* A second line only where a family has one to give — today
                    the confirmation's lifecycle and delivery state (KND-4b).
                    It wraps rather than truncates: the first line is cut on
                    a phone, and this one carries what the row is read for. */}
                {item.detail ? (
                  <p className="m-0 break-words text-[12px] text-muted-foreground">
                    {item.detail}
                  </p>
                ) : null}
              </div>
              {item.canOpen ? (
                <button
                  type="button"
                  onClick={() => handleOpen(item)}
                  disabled={openingKey === item.key}
                  aria-label={item.openLabel || `${item.title} öffnen`}
                  className={SECONDARY_BUTTON}
                >
                  Öffnen
                </button>
              ) : (
                // WHAT IS KNOWN, AND NOTHING MORE. `canOpen` is a pure
                // existence check on the record's own key — `has_pdf`,
                // `pdf_object_key`, `protocol_pdf_object_key`
                // (tenancyDocuments.js). Falsy collapses several different
                // stories into one: never generated, generation failed, a
                // draft inspection that has nothing to print yet, a file
                // invalidated by a later edit. This tab cannot tell them
                // apart, so it names the state and not a cause — "fehlt",
                // "fehlgeschlagen" or "noch nicht erstellt" would each be a
                // guess, and "noch" would promise a file that may never come.
                // A button here would 404; this tab does not generate.
                <span className="shrink-0 text-[12px] text-muted-foreground">
                  PDF nicht verfügbar
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
      </Card>

      {state.failed.length > 0 ? (
        <p
          className="m-0 text-[12px] text-muted-foreground"
          data-testid="tenancy-documents-partial"
        >
          Nicht geladen:{" "}
          {state.failed.map((s) => SOURCE_LABEL[s] || s).join(", ")}.
        </p>
      ) : null}

      {openError ? (
        <p role="alert" className="m-0 text-[12px] text-rose-700 dark:text-rose-300">
          {openError}
        </p>
      ) : null}
    </div>
  );
}
